import { ValuationService } from "src/services";
import vehicleService from "src/services/vehicle-service";
import { useValuationStore } from "src/stores/valuation-store";
import { logEvent, EVENTS } from 'src/functions';
import type { Valuation, CapValuationResponse, AutoTraderValuation } from "src/types/valuations";
import { AUTOTRADER_ENABLED } from "src/const/feature-flags";

export class ValuationError extends Error {
    constructor(message: string, public code: string) {
        super(message);
        this.name = 'ValuationError';
    }
}

export default async function getAndSaveValuation(vrm: string, mileage: number, affiliateUserId: string, force = false, dealerId?: number) {

    try {
        await useValuationStore().getValuations();

        const now = new Date();
        const ex = useValuationStore().valuations?.find(v =>
            v.vrm.replace(/ /g, '').toLocaleLowerCase() === vrm.replace(/ /g, '').toLocaleLowerCase() &&
            v.vehicleMileage === mileage &&
            v.status === 1 &&
            v.created &&
            new Date(v.created).getFullYear() === now.getFullYear() &&
            new Date(v.created).getMonth() === now.getMonth());

            console.log("Existing Valuation Check:", ex, "Force:", force)
        if (ex && !force) return { valuation: ex, autoTraderValuation: null, exists: true };

        let capVehicle;
        try {
            capVehicle = await vehicleService.vehicleLookup(vrm, mileage, false, true);
            console.log("CAP Vehicle Lookup Result:", capVehicle);
        } catch {
            throw new ValuationError(
                'Vehicle not found. Please check the registration and try again.',
                'VEHICLE_NOT_FOUND'
            );
        }

        let capValuation: CapValuationResponse | null = null;
        let autoTraderValuation: AutoTraderValuation | null = null;

        const tasks = [] as Promise<any>[];

        tasks.push(ValuationService.capValuation(
            capVehicle.capId,
            mileage,
            capVehicle.vrm,
            new Date(capVehicle.vehicleDateOfFirstReg ?? capVehicle.registrationDate).getFullYear()
        ).then((res) => {
            capValuation = res;
        }));
        if (AUTOTRADER_ENABLED) {
            tasks.push(ValuationService.getAutoTraderValuation(
                Number(affiliateUserId),
                capVehicle.vrm,
                mileage
            ).then((res) => {
                console.log("Auto Trader Valuation:", res);
                autoTraderValuation = res;
            }).catch(() => {
                autoTraderValuation = null;
            }));
        }

        await Promise.all(tasks);

        if (!capValuation && !autoTraderValuation) {
            throw new ValuationError(
                'No valuation data found for this vehicle. CAP and Autotrader returned no results.',
                'NO_VALUATION_DATA'
            );
        }

        const cap = capValuation ? capValuation as CapValuationResponse : null;
        const autoTrader = autoTraderValuation ? autoTraderValuation as AutoTraderValuation : null;

        const v = {
            affiliateUserId: affiliateUserId!.toString(),
            vrm: capVehicle.vrm,
            vehicleMileage: mileage,
            make: capVehicle.make,
            model: capVehicle.model,
            year: capVehicle.vehicleDateOfFirstReg ? new Date(capVehicle.vehicleDateOfFirstReg).getFullYear() : (capVehicle.registrationDate ? new Date(capVehicle.registrationDate).getFullYear() : null),
            fuelType: capVehicle.fuel,
            transmission: capVehicle.transmission,
            capCreatedOn: cap?.createdOn ? new Date(cap.createdOn) : null,
            capRetail: cap?.retailValue ? cap.retailValue : 0,
            capClean: cap?.cleanValue ? cap.cleanValue : 0,
            capAverage: cap?.averageValue ? cap.averageValue : 0,
            capBelow: cap?.belowValue ? cap.belowValue : 0,
            autoTraderCreatedOn: autoTrader ? new Date() : null,
            autoTraderRetail: autoTrader?.retail ? autoTrader.retail : 0,
            autoTraderPartExchange: autoTrader?.partExchange ? autoTrader.partExchange : 0,
            autoTraderTrade: autoTrader?.trade ? autoTrader.trade : 0,
            autoTraderPrivate: autoTrader?.private ? autoTrader.private : 0,
            created: new Date(),
            vehicleDetails: `${capVehicle.make} ${capVehicle.model} (${new Date(capVehicle.vehicleDateOfFirstReg ?? capVehicle.registrationDate).getFullYear()})`,
            trim: capVehicle.trim
        } as Valuation;

        const response = await useValuationStore().createValuation(v);

        if (response) {
            return { valuation: v, autoTraderValuation, exists: false };
        }

        throw new ValuationError(
            'Failed to save the valuation. Please try again.',
            'SAVE_FAILED'
        );
    }
    catch (error) {
        if (error instanceof ValuationError) {
            throw error;
        }
        console.error('Error in getAndSaveValuation:', error);
        throw new ValuationError(
            'Something went wrong while running the valuation. Please try again.',
            'UNKNOWN'
        );
    }
}
